import React, { useState, useEffect, useGlobal } from "reactn";
import axios from "axios";
import moment from "moment";
import startOfYear from "date-fns/startOfYear";
import endOfYear from "date-fns/endOfYear";

import { useDateFilter } from "../../../providers/reports";
import {
  DateFilter,
  ReportContainer,
  ReportTable
} from "../../../components/UI/reportsAnalytics";

import { formatMoney } from "../../../utilities/reportsAnalytics";
import { useCancellableRequest } from "../../../utilities/hooks/useCancellableRequest";

const yearStartDate = moment(startOfYear(new Date())).format("YYYY-MM-DD");
const yearEndDate = moment(endOfYear(new Date())).format("YYYY-MM-DD");

const ExpenseReport = () => {
  const [category, setCategory] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [expenseOverview, setExpenseOverview] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedCategoryId, setSelectedCategoryId] = useState();
  const [loading, setLoading] = useGlobal("loading");
  const { makeRequest } = useCancellableRequest();
  const [submittedFilters, setSubmittedFilters] = useState(null);
  const appliedDateFrom = submittedFilters?.from;
  const appliedDateTo = submittedFilters?.to;
  const appliedCategory = submittedFilters?.category;
  const appliedCategoryId = submittedFilters?.categoryId;
  const appliedFilterType = submittedFilters?.filterType;
  const appliedSearch = submittedFilters?.search?.trim().toLowerCase();
  const [searchText, setSearchText] = useState("");

  const [partnerPathway, setPartnerPathway] = useGlobal("partnerPathway");

  const [dateFrom, dateTo, setDateFrom, setDateTo] = useDateFilter(
    yearStartDate,
    yearEndDate
  );

  const [filterByCategory, setFilterByCategory] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState("");
  const [filteredExpenses, setFilteredExpenses] = useState([]);

  /**
   * Initially loads the required data for the component
   */
  useEffect(() => {
    const fetchExpenseData = async () => {
      setLoading(true);
      try {
        const result = await makeRequest({
          method: "get",
          url: `${process.env.REACT_APP_API}/partner/expense`
        });

        if (!result) return;

        if (result.data.success) {
          setCategory(result.data.data.categories);
          setExpenses(result.data.data.expenses);
          setFilteredExpenses(result.data.data.expenses);
          setExpenseOverview(result.data.data.expensesOverview);
        }
      } catch (error) {
        console.error("Error fetching expense data:", error);
      } finally {
        setLoading(false);
      }
    };

    setPartnerPathway([
      ...partnerPathway.slice(0, 1),
      { label: "Reports & Analytics", to: "/reporting" },
      { label: "Expense Report", to: "/reports/expense-report" }
    ]);
    fetchExpenseData();
  }, []);

  /**
   *
   * @param {event from the onchange input filters} event
   * sets the filtered items in the state
   */
  const filteredExpenseItems = event => {
    let filteredExpenseData = [...expenses];
    let filteredByCategory = filterByCategory;

    if (event.target.name === "category" && event.target.value === "All") {
      setFilterByCategory(filteredExpenseData);
    } else if (event.target.name === "category" && event.target.value !== "") {
      const filterCategoryId = category.filter(
        catFil => catFil.name === event.target.value
      )[0].id;
      filteredByCategory = filteredExpenseData.filter(
        e => e.categoryId === filterCategoryId
      );
      setFilterByCategory(filteredByCategory);
      filteredExpenseData = filteredByCategory;
    }

    if (event.target.name === "filterSearch" && selectedFilter === "Vendor") {
      const eventLowerCaseTrim = event.target.value.trim().toLowerCase();
      let filterByVendor = [];
      if (filterByCategory.length === 0) {
        filterByVendor = [...expenses];
      }
      if (filterByCategory.length > 0) {
        filterByVendor = filterByCategory;
      }
      filteredExpenseData = filterByVendor.filter(e =>
        e.vendor.toLowerCase().includes(eventLowerCaseTrim)
      );
    }

    if (
      event.target.name === "filterSearch" &&
      selectedFilter === "Description"
    ) {
      const eventLowerCaseTrim = event.target.value.trim().toLowerCase();
      let filterByDescription = [];
      if (filterByCategory.length === 0) {
        filterByDescription = [...expenses];
      }
      if (filterByCategory.length > 0) {
        filterByDescription = filterByCategory;
      }
      filteredExpenseData = filterByDescription.filter(e =>
        e.description.toLowerCase().includes(eventLowerCaseTrim)
      );
    }

    if (event.target.name === "filterSearch" && selectedFilter.length === 0) {
      let filterReset = [];
      if (filterByCategory.length === 0) {
        filterReset = [...expenses];
      }
      if (filterByCategory.length > 0) {
        filterReset = filterByCategory;
      }
      filteredExpenseData = filterReset;
    }

    setFilteredExpenses(filteredExpenseData);
  };

  /**
   * This function returns the Expense Items to be displayed in JSX
   */
  // const displayExpense = () => {
  //   return filteredExpenses.map(expense => {
  //     const expenseDate = new Date(expense.expense_date);
  //     const selectedDateFrom = new Date(dateFrom).getTime();
  //     const tempDateTo = new Date(dateTo);
  //     // Offset by a day to provide the correct filter.
  //     const selectedDateTo = tempDateTo.setDate(tempDateTo.getDate() + 1);

  //     if (expenseDate < selectedDateFrom || selectedDateTo < expenseDate) {
  //       return [];
  //     }

  //     return [
  //       moment(expenseDate).format("YYYY-MM-DD"),
  //       `${
  //         expense.vendor ? `${expense.vendor.substring(0, 40)}` : "No vendor"
  //       }`,
  //       `${
  //         expense.description
  //           ? ` - ${expense.description.substring(0, 40)}`
  //           : "No Description"
  //       }`,
  //       formatMoney(expense.grand_total)
  //     ];
  //   });
  // };

  const displayExpense = () => {
    if (!submittedFilters) return [];

    let data = [...expenses];

    // Category filter
    if (appliedCategory && appliedCategory !== "All" && appliedCategoryId) {
      data = data.filter(e => e.categoryId === appliedCategoryId);
    }

    // Vendor / Description search
    if (appliedSearch && appliedFilterType === "Vendor") {
      data = data.filter(e => e.vendor?.toLowerCase().includes(appliedSearch));
    }

    if (appliedSearch && appliedFilterType === "Description") {
      data = data.filter(e =>
        e.description?.toLowerCase().includes(appliedSearch)
      );
    }

    // Date filter
    const selectedDateFrom = new Date(appliedDateFrom).getTime();
    const tempDateTo = new Date(appliedDateTo);
    const selectedDateTo = tempDateTo.setDate(tempDateTo.getDate() + 1);

    data = data.filter(expense => {
      const expenseDate = new Date(expense.expense_date).getTime();
      return expenseDate >= selectedDateFrom && expenseDate <= selectedDateTo;
    });

    return data.map(expense => {
      const expenseDate = new Date(expense.expense_date);
      return [
        moment(expenseDate).format("YYYY-MM-DD"),
        expense.vendor ? expense.vendor.substring(0, 40) : "No vendor",
        expense.description
          ? expense.description.substring(0, 40)
          : "No Description",
        formatMoney(expense.grand_total)
      ];
    });
  };

  // const displayTotalExpense = () => {
  //   const filteredData = [...filteredExpenses];
  //   const sumFunction = (r, c) => {
  //     const expenseDate = new Date(c.expense_date);
  //     const selectedDateFrom = new Date(dateFrom);
  //     const selectedDateTo = new Date(dateTo);

  //     if (expenseDate < selectedDateFrom || selectedDateTo < expenseDate) {
  //       return r + 0;
  //     }

  //     return r + parseFloat(c.grand_total);
  //   };
  //   return formatMoney(filteredData.reduce(sumFunction, 0));
  // };

  const displayTotalExpense = () => {
    if (!submittedFilters) return formatMoney(0);

    const data = displayExpense();
    const sum = data.reduce((r, row) => {
      const amount = parseFloat(row[3].replace(/[^0-9.-]+/g, ""));
      return r + amount;
    }, 0);

    return formatMoney(sum);
  };

  const csvHeaders = [
    { label: "Date", key: "date" },
    { label: "Vendor", key: "vendor" },
    { label: "Expense Description", key: "description" },
    { label: "Amount", key: "amount" }
  ];

  /**
   * Gets the CSV for the CSVLink component to use.
   *
   * @returns {Array<Object>} Each property of each object corresponds to a column.
   */
  const getCsvData = () => {
    const data = displayExpense();
    let csvRows = data.map(expense => {
      return {
        date: expense[0],
        vendor: expense[1],
        description: expense[2],
        amount: expense[3]
      };
    });

    csvRows.push({
      date: "TOTAL",
      amount: `$${displayTotalExpense()}`
    });
    return csvRows;
  };

  const reportSubtitles = submittedFilters
    ? [
        appliedCategory || "All",
        `For ${moment(appliedDateFrom).format("MMM D, YYYY")} - ${moment(
          appliedDateTo
        ).format("MMM D, YYYY")}`
      ]
    : ["Select filters and click Apply"];
  return (
    <div className="admin">
      <div className="container-fluid adminprofiles">
        <div className="row cont">
          <div className="col-md-6">
            <h1>Expense Report</h1>
          </div>
        </div>

        <div className="filter-container">
          {/* FIRST ROW: Just the Date Filters */}
          <div
            className="filter-section"
            style={{
              display: "flex",
              marginBottom: "10px"
            }}
          >
            <DateFilter
              dateFrom={dateFrom}
              setDateFrom={setDateFrom}
              dateTo={dateTo}
              setDateTo={setDateTo}
            />
          </div>

          {/* SECOND ROW: Category, Filter, Search, and Apply Button */}
          <div
            className="filter-section"
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "flex-end", // Aligns button with inputs
              gap: "10px",
              marginTop: "0px"
            }}
          >
            {/* Category */}
            <div className="filter-item">
              <label className="filter-label">Category</label>
              <select
                name="category"
                className="form-control filter-input"
                value={selectedCategory}
                onChange={e => {
                  setSelectedCategory(e.target.value);
                  if (e.target.value === "All") {
                    setSelectedCategoryId("");
                  } else if (e.target.value) {
                    const id = category.find(
                      c => c.name === e.target.value
                    )?.id;
                    setSelectedCategoryId(id);
                  } else {
                    setSelectedCategoryId("");
                  }
                }}
              >
                <option value="All">All</option>
                {category.map(cat => (
                  <option key={cat.id} value={cat.name}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Filter Type */}
            <div className="filter-item">
              <label className="filter-label">Filter</label>
              <select
                name="filterBy"
                className="form-control filter-input"
                value={selectedFilter}
                onChange={e => setSelectedFilter(e.target.value)}
              >
                <option value="">Please Select</option>
                <option value="Vendor">Vendor</option>
                <option value="Description">Description</option>
              </select>
            </div>

            {/* Search */}
            <div className="filter-item">
              <label className="filter-label">Search</label>
              <input
                type="text"
                className="form-control"
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                placeholder="Type to search…"
                style={{ width: "200px" }}
              />
            </div>

            {/* Apply Button */}
            <button
              className="btn btn-primary"
              style={{ height: "38px" }} // Height matches standard Bootstrap inputs
              onClick={() => {
                setSubmittedFilters({
                  from: dateFrom,
                  to: dateTo,
                  category: selectedCategory,
                  categoryId: selectedCategoryId,
                  filterType: selectedFilter,
                  search: searchText
                });
              }}
            >
              Apply Filters
            </button>
          </div>
        </div>
        <ReportContainer
          title="Expense Report"
          subtitles={reportSubtitles}
          csvHeaders={csvHeaders}
          csvData={submittedFilters ? getCsvData() : []}
          fileName={
            submittedFilters
              ? `Expense_Report_${appliedDateFrom}-${appliedDateTo}.csv`
              : "Expense_Report.csv"
          }
        >
          <div className="report-table-large">
            <ReportTable
              headings={[
                { label: "Summary", className: "table-item-bold" },
                {
                  label: "Total",
                  className: "table-item-money",
                  style: { textAlign: "right" }
                }
              ]}
              body={
                submittedFilters
                  ? [["Total Expense", displayTotalExpense()]]
                  : []
              }
            />
            <ReportTable
              headings={[
                { label: "Date", className: "table-item-bold" },
                { label: "Vendor" },
                { label: "Expense Description" },
                {
                  label: "Total",
                  className: "table-item-money",
                  style: { textAlign: "right" }
                }
              ]}
              body={submittedFilters ? displayExpense() : []}
              noDataText="No expenses found in this category."
            />
          </div>
          {!submittedFilters && (
            <p style={{ textAlign: "center", marginTop: 20 }}>
              Select filters and press Apply to view report.
            </p>
          )}
        </ReportContainer>
      </div>
    </div>
  );
};

export default ExpenseReport;
